import { Avatar, Box, DialogContent, Modal } from "@mui/material"
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { memo, RefObject, useEffect, useState } from "react"
import { useParams } from "react-router"
import emptyGroup from "src/components/images/empty_group.png"
import empty from "src/components/images/empty.png"
import { useSearchSliders } from "src/hooks/use-search-sliders"

import { DGText } from "../../components/common/DGText"
import emptyAvatar from "../../components/images/empty_avatar.png"
import { ProgressSpinner } from "../../components/ProgressSpinner"
import { Slider, TTeaser } from "../../components/Sliders/Slider"
import {
  Album,
  Artist,
  EditorialPlaylist,
  FacetType,
  Group,
  LiveConcert,
  Partner,
  SearchByFacetValueQuery,
  Video,
  VodConcert,
} from "../../generated/graphql"
import useSdk, { ErrorLevel } from "../../hooks/use-sdk"
import { useSmoothNavigate } from "../../hooks/use-smooth-navigate"
import { useTransitions } from "../../hooks/use-transitions"
import useTranslate, { Namespaces } from "../../hooks/use-translate"
import { appStore } from "../../store/AppStore"
import { TranslationKey } from "../../types"
import { s } from "../../utilities/common"
import { theme, TI } from "../../utilities/theme"
import { useFocusHelper } from "../../utilities/use-focus-helper"
import { usePageviewTracking } from "../Detail/use-pageview-tracking"

import { useFacetDetailTracking } from "./use-facet-detail-tracking"

export const mapAbstractFacetType: Record<
  FacetType,
  "Artist" | "Partner" | "Group"
> = {
  [FacetType.Soloists]: "Artist",
  [FacetType.Composers]: "Artist",
  [FacetType.Conductors]: "Artist",
  [FacetType.Ensembles]: "Group",
  [FacetType.Venue]: "Partner",
}

export const mapAbstractQueryType: Record<
  string,
  | Video["__typename"]
  | VodConcert["__typename"]
  | Album["__typename"]
  | Artist["__typename"]
  | Partner["__typename"]
  | LiveConcert["__typename"]
  | Group["__typename"]
  | EditorialPlaylist["__typename"]
> = {
  SearchVideo: "Video",
  SearchVodConcert: "VodConcert",
  SearchAlbum: "Album",
  SearchArtist: "Artist",
  SearchPartner: "Partner",
  SearchLiveConcert: "LiveConcert",
  SearchGroup: "Group",
  SearchEditorialPlaylist: "EditorialPlaylist",
}

const mapFacetToSubtitle: Record<
  FacetType,
  TranslationKey<Namespaces.Default>
> = {
  [FacetType.Soloists]: "api__type_role_soloist",
  [FacetType.Composers]: "api__type_role_composer",
  [FacetType.Conductors]: "api__type_role_conductor",
  [FacetType.Ensembles]: "api__type_group_ensemble",
  [FacetType.Venue]: "api__type_partner_venue",
}

const FacetCard = memo(
  ({
    id,
    imageUrl,
    title,
    scrollRef,
    facetType,
  }: {
    id: string
    imageUrl: string
    title: string
    scrollRef: RefObject<HTMLElement>
    facetType: FacetType
  }) => {
    const navigate = useSmoothNavigate()
    const t = useTranslate()
    const onPressAction = () => {
      navigate(`../detail/${mapAbstractFacetType[facetType]}/${id}/0/0/0`)
    }
    const { ref, focused, focusSelf } = useFocusable({
      onFocus: (x, _, query) => {
        if (!(query && query.noScroll) && scrollRef.current)
          scrollRef.current.style.transform = "translate3d(0, 0, 0)"
      },
      onEnterPress: onPressAction,
    })
    return (
      <Box
        onMouseDown={() => onPressAction()}
        onMouseEnter={() => focusSelf({ noScroll: true })}
        ref={ref}
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Box
          sx={{
            mt: s("110px"),
            mb: s("36px"),
            background: focused
              ? theme.palette.secondary.variant
              : "transparent",
            pl: s("32px"),
            pr: s("32px"),
            pt: s("28px"),
            pb: s("28px"),
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: focused
              ? `${s("0px 30px 112px 0px")} rgba(0, 0, 0, 0.60)`
              : undefined,
            borderRadius: s("16px"),
            transform: focused ? "scale(1.05)" : undefined,
          }}
        >
          <Avatar
            src={imageUrl}
            sx={{
              height: s("164px"),
              width: s("164px"),
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${
                facetType === "VENUE"
                  ? empty
                  : facetType !== "ENSEMBLES"
                    ? emptyAvatar
                    : emptyGroup
              })`,
              backgroundPosition: "center",
              backgroundSize:
                (facetType === "VENUE"
                  ? s("180")
                  : facetType === "ENSEMBLES"
                    ? s("120")
                    : s("74")) + "px",
              backgroundColor: theme.palette.primary.main,
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", ml: s("36px") }}>
            <DGText
              tx={TI.Subheadline04tv}
              sx={{
                color: "white",
              }}
            >
              {title}
            </DGText>
            <DGText
              tx={TI.Subheadline06tv}
              sx={{
                color: theme.palette.white[55],
              }}
            >
              {t(mapFacetToSubtitle[facetType])}
            </DGText>
          </Box>
        </Box>
      </Box>
    )
  },
)

const Screen = ({
  id,
  input,
}: {
  id?: string
  input?: SearchByFacetValueQuery
}) => {
  const { ref, focusKey, focusSelf } = useFocusable({
    isFocusBoundary: true,
  })
  const sliders = useSearchSliders(input)
  const { type: tType } = useParams()
  const type = tType as FacetType

  useEffect(() => {
    if (input) {
      focusSelf()
      getFocus()
      setSpinner("out")
    }
  }, [input, focusSelf])

  const { getFocus, addFocus } = useFocusHelper()
  const navigate = useSmoothNavigate()
  const setSpinner = appStore((state) => state.setSpinner)
  const trackPageview = usePageviewTracking()
  const trackDetailPage = useFacetDetailTracking({
    content: input,
    contentType: type,
  })
  const transition = useTransitions()

  useEffect(() => {
    if (input && id) {
      trackPageview(
        "SearchFacetCollection",
        `search/${type.toLowerCase()}/${id}`,
      )
      trackDetailPage()
    }
  }, [input, id])

  if (!input || !input.searchByFacetValue) return <ProgressSpinner />
  const data = input.searchByFacetValue
  return (
    <FocusContext.Provider value={focusKey}>
      <Box ref={ref} sx={{ transition }} className={"loading_in"}>
        <FacetCard
          id={data.id}
          facetType={type}
          scrollRef={ref}
          title={data.title}
          imageUrl={data.imageUrl}
        />
        {sliders &&
          sliders.map((slider, sliderIndex) => (
            <Slider
              key={slider.title + slider.id}
              onPressTitle={(d?: TTeaser, teaserIndex?: number) => {
                addFocus()
                if (!d || teaserIndex === undefined) return
                ref.current?.classList.add("loading_out")
                navigate(
                  `../detail/${d.type}/${d.id}/${sliderIndex + 1}/${
                    teaserIndex + 1
                  }/${slider.id}`,
                )
              }}
              onPressBrowse={() => {
                addFocus()
                ref.current?.classList.add("loading_out")
                navigate(
                  `../browse/${type}-${mapAbstractQueryType[slider.id]}-${
                    slider.node
                  }/${data.id}/1/0`,
                )
              }}
              onFocus={({ top: y }, _, query) => {
                if (!(query && query.noScroll)) {
                  ref.current.style.transform =
                    "translate3d(0, " + (-y + s(300)) + "px, 0)"
                }
              }}
              title={slider.title}
              data={slider}
              dimensions={slider.style}
              margin={true}
            />
          ))}
      </Box>
    </FocusContext.Provider>
  )
}

export const FacetDetailScreen = () => {
  const sdk = useSdk(ErrorLevel.WithPrompt)
  const { id, type: tType } = useParams()
  const type = tType as FacetType
  const [data, setData] = useState<SearchByFacetValueQuery>()

  useEffect(() => {
    if (id && type)
      sdk
        .searchByFacetValue({ value: id, type, sliderPageSize: 7 })
        .then((res) => setData(res))
  }, [id, type])

  return (
    <Modal
      key={"facetdetailscreen" + id}
      open={true}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.main,
      }}
    >
      <DialogContent
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: theme.palette.primary.main,
        }}
      >
        <Screen id={id} input={data} />
      </DialogContent>
    </Modal>
  )
}
